import type { DemoRule } from "../domain/ruleTypes";

export const SEEDED_RULES: DemoRule[] = [
    {
        id: "DQ-001",
        category: "Data quality",
        scope: "part",
        name: "Mandatory attribute completeness",
        capability: "Category-specific required fields and quarantine routing",
        output: "Missing semiconductor attributes route to quarantine with reason codes.",
        examplePrompt: "Require package, pin count, voltage range, and compliance status before publish.",
    },
    {
        id: "DQ-002",
        category: "Data quality",
        scope: "part",
        name: "Unit-of-measure consistency",
        capability: "Unit normalization checks and human-review routing",
        output: "Passive components with unresolved unit conflicts land in pending review.",
    },
    {
        id: "DQ-003",
        category: "Data quality",
        scope: "part",
        name: "Lifecycle status gating",
        capability: "Lifecycle enum gates that suppress downstream publish",
        output: "EOL and NRND parts route to archive instead of active catalog.",
        examplePrompt: "Archive lifecycle statuses that are EOL or not recommended for new design.",
    },
    {
        id: "DQ-004",
        category: "Data quality",
        scope: "part",
        name: "MPN format validation",
        capability: "Manufacturer-aware format checks and placeholder detection",
        output: "Suspicious MPNs are quarantined before they enter downstream systems.",
    },
    {
        id: "TR-005",
        category: "Transformation",
        scope: "part",
        name: "Taxonomy to ERP product group",
        capability: "Value-table mapping from source taxonomy to ERP and CRM categories",
        output: "Known taxonomy paths map to ERP product group, product line, and CRM family.",
    },
    {
        id: "TR-006",
        category: "Transformation",
        scope: "part",
        name: "ERP-safe description formatting",
        capability: "Priority-preserving text construction for downstream field limits",
        output: "Description previews preserve manufacturer, MPN, package, voltage, and tolerance.",
    },
    {
        id: "TR-007",
        category: "Transformation",
        scope: "part",
        name: "CRM-ready product name construction",
        capability: "Text merge from normalized attributes into sales-facing product names",
        output: "Product-name previews hide raw supplier strings from CRM line items.",
    },
    {
        id: "TR-008",
        category: "Transformation",
        scope: "price_update",
        name: "Supplier price tolerance table",
        capability: "Manufacturer-specific thresholds owned by the business",
        output: "Price changes auto-approve, flag, or hold by rule-table row.",
        examplePrompt: "Hold Texas Instruments price increases above 8% and notify procurement.",
    },
    {
        id: "WF-009",
        category: "Workflow",
        scope: "part",
        name: "New part introduction alert",
        capability: "Existence lookup and CRM product-create event generation",
        output: "Net-new MPNs create product-review events for product owners.",
    },
    {
        id: "WF-010",
        category: "Workflow",
        scope: "price_update",
        name: "Price increase notification trigger",
        capability: "Price threshold breach joined to open opportunity activity",
        output: "Open opportunities receive margin-risk alert events before stale prices are quoted.",
    },
    {
        id: "WF-011",
        category: "Workflow",
        scope: "part",
        name: "Obsolescence cross-sell flag",
        capability: "Lifecycle change joined to open commercial activity",
        output: "Open opportunities and purchase orders receive alternate-part alerts.",
    },
    {
        id: "WF-012",
        category: "Workflow",
        scope: "part",
        name: "Compliance change escalation",
        capability: "Prior/current compliance comparison and review gate",
        output: "Compliance regressions route to review instead of silently overwriting prior values.",
    },
    {
        id: "CF-013",
        category: "Confidence",
        scope: "part",
        name: "Enrichment confidence threshold",
        capability: "Score-based promotion to published or pending-review layers",
        output: "Low-confidence records stay out of downstream publish queues.",
    },
    {
        id: "CF-014",
        category: "Confidence",
        scope: "part",
        name: "Attribute source hierarchy override",
        capability: "Source-priority conflict resolution with an auditable decision",
        output: "Manufacturer-direct values outrank enrichment and supplier-submitted values.",
    },
];

export const getSeededRule = (ruleId: string): DemoRule => {
    const rule = SEEDED_RULES.find((candidate) => candidate.id === ruleId);
    if (!rule) throw new Error(`Unknown seeded rule '${ruleId}'.`);
    return rule;
};

export type RuleMetadataMap = Record<string, DemoRule>;

export const cloneDemoRule = (rule: DemoRule): DemoRule => JSON.parse(JSON.stringify(rule)) as DemoRule;

export const cloneSeededRuleMetadataMap = (): RuleMetadataMap =>
    Object.fromEntries(SEEDED_RULES.map((rule) => [rule.id, cloneDemoRule(rule)]));

export const cloneSeededRuleOrder = (): string[] => SEEDED_RULES.map((rule) => rule.id);
